import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useCommande, useDeleteCommande, useRegenerateLink, useRunAction, useSettings } from '../api/hooks';
import type { Action } from '../api/types';
import { Trash2 } from 'lucide-react';
import { Button, ErrorBox, Section, Spinner, Tabs } from '../components/ui';
import { useToast } from '../components/toast';
import JournalTable from '../components/JournalTable';
import CommandeHeader from '../components/commande/CommandeHeader';
import ReglagesCard from '../components/commande/ReglagesCard';
import ReglagesCommande from '../components/commande/ReglagesCommande';
import LiensSection from '../components/commande/LiensSection';
import BilletCard from '../components/commande/BilletCard';
import GestionQrCodes from '../components/commande/GestionQrCodes';
import ApercuApp from '../components/commande/ApercuApp';
import TransfertsSection from '../components/commande/TransfertsSection';

const ONGLETS = ['commande', 'billets', 'api', 'journal'] as const;
type Onglet = (typeof ONGLETS)[number];

export default function CommandeDetail() {
  const { guid = '' } = useParams();
  const navigate = useNavigate();
  // L'onglet est dans l'URL (?onglet=billets) : il survit au rafraîchissement et se partage.
  const [searchParams, setSearchParams] = useSearchParams();
  const brut = searchParams.get('onglet');
  const onglet: Onglet = (ONGLETS as readonly string[]).includes(brut ?? '') ? (brut as Onglet) : 'commande';
  const setOnglet = (next: Onglet) =>
    setSearchParams(next === 'commande' ? {} : { onglet: next }, { replace: true });
  const toast = useToast();

  const { data, isPending, error } = useCommande(guid);
  // L'app décide du dévoilage du QR sur l'heure serveur (en-tête `Date`), pas sur celle du navigateur.
  const settings = useSettings();
  const runAction = useRunAction(guid);
  const regenerate = useRegenerateLink(guid);
  const remove = useDeleteCommande();

  const onAction = (action: Action) =>
    runAction.mutate(action, {
      onSuccess: () => toast.success(`Action ${action.action} appliquée`),
      onError: (err) => toast.error(err),
    });

  /** Enchaîne plusieurs actions dans l'ordre ; s'arrête à la première erreur. */
  const onActions = async (actions: Action[], message: string) => {
    try {
      for (const action of actions) await runAction.mutateAsync(action);
      toast.success(message);
    } catch (err) {
      toast.error(err);
    }
  };

  if (error) return <ErrorBox error={error} />;
  if (isPending || !data) return <Spinner />;

  const { commande, billets, tokens, transferts, preview } = data;
  const now = settings.data ? new Date(settings.data.serverNow) : new Date();

  const supprimer = () => {
    if (!window.confirm('Supprimer définitivement cette commande, ses billets, tokens et transferts ?')) return;
    remove.mutate(guid, {
      onSuccess: () => {
        toast.success('Commande supprimée');
        navigate('/');
      },
      onError: (err) => toast.error(err),
    });
  };

  const tabProps = (onglet: Onglet) => ({
    id: `commande-panel-${onglet}`,
    role: 'tabpanel' as const,
    'aria-labelledby': `commande-tab-${onglet}`,
    className: 'space-y-5',
  });

  return (
    <div className="space-y-5">
      <CommandeHeader
        commande={commande}
        billets={billets}
        now={now}
        onBack={() => navigate('/')}
      />

      <Tabs
        label="Sections de la commande"
        idPrefix="commande"
        options={[
          { value: 'commande', label: 'Commande' },
          { value: 'billets', label: `Billets (${billets.length})` },
          { value: 'api', label: 'API' },
          { value: 'journal', label: 'Journal' },
        ]}
        value={onglet}
        onChange={setOnglet}
      />

      {onglet === 'commande' ? (
        <div {...tabProps('commande')}>
          <ReglagesCard
            commande={commande}
            busy={runAction.isPending}
            onToggleFlag={(flag, value) => onAction({ action: 'setFlags', body: { [flag]: value } })}
          />
          <ReglagesCommande commande={commande} onAction={onAction} busy={runAction.isPending} />
          <LiensSection
            tokens={tokens}
            busy={regenerate.isPending}
            onRegenerate={() =>
              regenerate.mutate(undefined, {
                onSuccess: () => toast.success('Lien d’import régénéré'),
                onError: (err) => toast.error(err),
              })
            }
          />
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-error/40 p-4">
            <p className="text-sm text-on-surface-variant">
              Supprime la commande, ses billets, ses liens et ses transferts du BO. Irréversible.
            </p>
            <Button size="sm" variant="danger" onClick={supprimer}>
              <Trash2 size={15} aria-hidden />
              Supprimer la commande
            </Button>
          </div>
        </div>
      ) : null}

      {onglet === 'billets' ? (
        <div {...tabProps('billets')}>
          <GestionQrCodes
            key={`${commande.envoiCodeBarreDiffere}-${commande.dateAffichageCodeBarre ?? ''}`}
            commande={commande}
            billets={billets}
            now={now}
            onActions={(actions, message) => void onActions(actions, message)}
            busy={runAction.isPending}
          />
          <div className="space-y-3">
            {billets.map((billet) => (
              <BilletCard key={billet.idBillet} billet={billet} onAction={onAction} busy={runAction.isPending} />
            ))}
          </div>
          <TransfertsSection transferts={transferts} />
        </div>
      ) : null}

      {onglet === 'api' ? (
        <div {...tabProps('api')}>
          <ApercuApp preview={preview} />
        </div>
      ) : null}

      {onglet === 'journal' ? (
        <div {...tabProps('journal')}>
          <Section title="Journal de cette commande">
            <JournalTable filters={{ guid }} />
          </Section>
        </div>
      ) : null}
    </div>
  );
}
